import {
  RECEIVE_FROM_API,
  RECEIVE_FROM_API_HOLDING_DATA,
  RECEIVE_INFOGRAPHICS_FROM_API,
  REQUEST_API,
  REQUEST_API_FAILURE,
  REQUEST_API_FAILURE_HOLDING_DATA,
  REQUEST_API_HOLDING_DATA,
  REQUEST_POST_ACR_INFO_BOX_SUCCESS,
} from 'src/constants';
import { REQUEST_API_LATEST, RESET_REPORTS_LIST } from 'src/redux/api/actions';
import type { BaseAction } from 'src/redux/api/types';
import { CLEAR_API_ERROR, CLEAR_API_KEYS } from 'src/redux/api/types';
import {
  COMMENT_REPLY_REQUEST_API,
  COMMENT_REQUEST_API,
} from 'src/redux/comments/actions';
import * as comments from 'src/redux/comments/reducer';
import {
  ACR_INFO_BOX_ACTION,
  ACR_REPORT_STATE_TRANSITION_ACTION,
  APPLY_CHANGE_TITLE_ACTION,
  CHANGE_INFOGRAPHICS_EDITOR,
  CLEAN_REPORT_DATA_ACTION,
  CLEAR_VIEW_DATA,
  REFRESH_SECTIONS_DATA_SOURCE_ACTION,
  RESET_DATA_NOTES_ACTION,
  UPDATE_DATA_NOTES_ACTION,
  UPDATE_NARRATIVE_IN_STORAGE,
  UPDATE_REVIEW_STATE_DATA_ACTION,
  UPDATE_USER_SETTINGS_ACTION,
  UPDATE_USER_SETTINGS_FAILED,
  UPDATE_USER_SETTINGS_SUCCESS,
} from 'src/redux/constants';
import * as infographics from 'src/redux/infographics/reducer';
import {
  CANCEL_MULTINARRATIVE_ACTION_SUCCESS,
  LOCK_MULTINARRATIVE_ACTION_SUCCESS,
  RECEIVE_EXTRA_NARRATIVE_FROM_API,
  RECEIVE_MULTI_NARRATIVE_FROM_API,
  RECEIVE_MULTI_NARRATIVE_SINGLE_FROM_API,
  RECEIVE_NARRATIVE_FROM_API,
  RESET_MULTINARRATIVE_ACTION,
  RESTORE_VERSION_MULTINARRATIVE_ACTION_SUCCESS,
  SAVE_MULTINARRATIVE_ACTION,
  SAVE_MULTINARRATIVE_ACTION_FAILED,
  SAVE_MULTINARRATIVE_ACTION_SUCCESS,
  SET_EXTRA_NARRATIVE_ACTION,
  SET_NARRATIVE_ACTION,
} from 'src/redux/narrative/actions';
import * as narrative from 'src/redux/narrative/reducer';
import * as infoBox from 'src/redux/review/reducer';
import { RECEIVE_DATA_NOTES_FROM_API } from 'src/redux/sections/actions';
import * as dataNotes from 'src/redux/sections/reducer';
import {
  SET_SUBTITLE_ACTION,
  SET_SUBTITLE_SUCCESS_ACTION,
} from 'src/redux/subtitle/actions';
import * as subtitle from 'src/redux/subtitle/reducer';
import * as userSettings from 'src/redux/userSettings/reducer';
import type { IViewDataInitialState } from 'src/redux/viewData/types';
import * as api from './apiLifecycle';
import { resolveStorageKey } from './helpers';

type ViewDataHandler = (
  state: IViewDataInitialState,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- cross-slice actions are loosely typed at dispatch; handlers re-type their own payload
  action: any,
  key: string,
) => IViewDataInitialState;

const HANDLERS: Record<string, ViewDataHandler> = {
  [REQUEST_API_HOLDING_DATA]: (s, _a, k) => api.requestApiHoldingData(s, k),
  [REQUEST_API]: (s, _a, k) => api.requestApi(s, k),
  [REQUEST_API_LATEST]: (s, _a, k) => api.requestApi(s, k),
  [RECEIVE_FROM_API]: (s, a, k) => api.receiveFromApi(s, a, k),
  [RECEIVE_FROM_API_HOLDING_DATA]: (s, a, k) =>
    api.receiveFromApiHoldingData(s, a, k),
  [REQUEST_API_FAILURE]: (s, a, k) => api.requestApiFailure(s, a, k),
  [REQUEST_API_FAILURE_HOLDING_DATA]: (s, a, k) =>
    api.requestApiFailureHoldingData(s, a, k),
  [CLEAR_API_KEYS]: (s, a) => api.clearApiKeys(s, a),
  [CLEAR_API_ERROR]: (s, _a, k) => api.clearApiError(s, k),
  [CLEAR_VIEW_DATA]: s => api.clearViewData(s),
  [CLEAN_REPORT_DATA_ACTION]: s => api.cleanReportData(s),
  [RESET_REPORTS_LIST]: s => api.resetReportsList(s),

  [REFRESH_SECTIONS_DATA_SOURCE_ACTION]: (s, a) =>
    api.setFetchingOnDataKey(s, a.dataKey),
  [ACR_INFO_BOX_ACTION]: (s, a) => api.setFetchingOnDataKey(s, a.dataKey),
  [UPDATE_REVIEW_STATE_DATA_ACTION]: (s, a) =>
    api.setFetchingOnDataKey(s, a.dataKey),
  [APPLY_CHANGE_TITLE_ACTION]: (s, a) => api.setFetchingOnDataKey(s, a.dataKey),
  [UPDATE_DATA_NOTES_ACTION]: (s, a) => api.setFetchingOnDataKey(s, a.dataKey),
  [ACR_REPORT_STATE_TRANSITION_ACTION]: (s, a) =>
    api.setFetchingOnDataKey(s, a.dataKey),
  [UPDATE_USER_SETTINGS_ACTION]: (s, a) =>
    api.setFetchingOnDataKey(s, a.dataKey),

  [UPDATE_NARRATIVE_IN_STORAGE]: (s, a) =>
    narrative.updateNarrativeInStorage(s, a),
  [RECEIVE_NARRATIVE_FROM_API]: (s, a) => narrative.receiveNarrative(s, a),
  [RECEIVE_MULTI_NARRATIVE_FROM_API]: (s, a) =>
    narrative.receiveMultiNarrative(s, a),
  [RECEIVE_MULTI_NARRATIVE_SINGLE_FROM_API]: (s, a) =>
    narrative.receiveMultiNarrativeSingle(s, a),
  [RECEIVE_EXTRA_NARRATIVE_FROM_API]: (s, a) =>
    narrative.receiveExtraNarrative(s, a),
  [LOCK_MULTINARRATIVE_ACTION_SUCCESS]: (s, a) =>
    narrative.lockMultiNarrativeSuccess(s, a),
  [CANCEL_MULTINARRATIVE_ACTION_SUCCESS]: (s, a) =>
    narrative.cancelMultiNarrativeSuccess(s, a),
  [SAVE_MULTINARRATIVE_ACTION]: (s, a) => narrative.saveMultiNarrative(s, a),
  [SAVE_MULTINARRATIVE_ACTION_FAILED]: s =>
    narrative.saveMultiNarrativeFailed(s),
  [SAVE_MULTINARRATIVE_ACTION_SUCCESS]: (s, a) =>
    narrative.saveMultiNarrativeSuccess(s, a),
  [RESET_MULTINARRATIVE_ACTION]: (s, a) => narrative.resetMultiNarrative(s, a),
  [RESTORE_VERSION_MULTINARRATIVE_ACTION_SUCCESS]: (s, a) =>
    narrative.restoreVersionMultiNarrativeSuccess(s, a),
  [SET_NARRATIVE_ACTION]: (s, a) => narrative.setNarrative(s, a),
  [SET_EXTRA_NARRATIVE_ACTION]: (s, a) => narrative.setExtraNarrative(s, a),

  [SET_SUBTITLE_ACTION]: (s, a) => subtitle.setSubtitle(s, a),
  [SET_SUBTITLE_SUCCESS_ACTION]: (s, a) => subtitle.setSubtitleSuccess(s, a),

  [RECEIVE_INFOGRAPHICS_FROM_API]: (s, a) =>
    infographics.receiveInfographics(s, a),
  [CHANGE_INFOGRAPHICS_EDITOR]: (s, a) =>
    infographics.changeInfographicsEditor(s, a),

  [RESET_DATA_NOTES_ACTION]: s => dataNotes.resetDataNotes(s),
  [RECEIVE_DATA_NOTES_FROM_API]: (s, a) => dataNotes.receiveDataNotes(s, a),

  [UPDATE_USER_SETTINGS_SUCCESS]: (s, a) =>
    userSettings.updateUserSettingsSuccess(s, a),
  [UPDATE_USER_SETTINGS_FAILED]: (s, a) =>
    userSettings.updateUserSettingsFailed(s, a),

  [COMMENT_REQUEST_API]: s => comments.commentRequest(s),
  [COMMENT_REPLY_REQUEST_API]: s => comments.commentRequest(s),

  [REQUEST_POST_ACR_INFO_BOX_SUCCESS]: (s, a) =>
    infoBox.receivePostAcrInfoBoxSuccess(s, a),
};

export const viewData = (
  state: IViewDataInitialState | undefined,
  incomingAction: BaseAction,
): IViewDataInitialState => {
  const currentState = state ?? {};
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- cross-slice actions are loosely typed at dispatch; handlers re-type their own payload
  const action = incomingAction as any;
  const key = resolveStorageKey(action) as string;

  const handler = HANDLERS[action.type];
  return handler ? handler(currentState, action, key) : currentState;
};
